import { HttpClient } from "@angular/common/http";
import { Injectable, NgZone } from "@angular/core";
import { Observable } from "rxjs";
import { map } from 'rxjs/operators';
import { AkitCreateSessionResponse, AkitCreateSessionsForExerciseResult,AuthoredExercise, ExerciseData, ExerciseResult, InteractionData, ValidateExercisResult} from "../types/algebrakit.types";
import { AKIT_CREATE_SESSION_ENDPOINT, AKIT_LOCK_SESSION_ENDPOINT, AKIT_SESSION_SCORE_ENDPOINT, AKIT_VALIDATE_SESSION_ENDPOINT, GET_EXERCISE_DATA_ENDPOINT, GET_EXERCISE_ENDPOINT, GET_SESSION_ENDPOINT } from "../types/request.types";

declare const AlgebraKIT:any;

const defaultOptions = {
    defaultAudience: {
        name: 'English',
        id: 'uk_KS5',
        languages: ['en']
    },
    defaultQuestionMode: 'ALL_AT_ONCE',
    defaultEditor: 'equations',
    availableAudiences: [{
        name: 'English Key Stage 5',
        id: 'uk_KS5',
        languages: ['en']
    }, {
        name: 'English Key Stage 3',
        id: 'uk_KS3',
        languages: ['en']
    }, {
        name: 'Espagnol',
        id: 'spain',
        languages: ['es', 'en']
    }, {
        name: 'François',
        id: 'fr_vwo_b',
        languages: ['fr', 'en']
    }, {
        name: 'Deutsch',
        id: 'de_vwo_b',
        languages: ['de', 'en']
    }, {
        name: 'Nederlands Bovenbouw',
        id: 'vwo-b',
        languages: ['nl', 'en']
    }, {
        name: 'Nederlands Onderbouw',
        id: 'onderbouw',
        languages: ['nl', 'en']
    }]
}

@Injectable({
    providedIn: "root",
})
export class AlgebraKiTService {
    _exerciseResults: Observable<AkitCreateSessionsForExerciseResult> =
        new Observable<AkitCreateSessionsForExerciseResult>();

    constructor(private http: HttpClient, private ngZone: NgZone) {}

    currentExerciseId: string;
    currentExercise: AuthoredExercise;

    createSingleSession(
        ex: string | AuthoredExercise,
        startActive?: boolean,
        assessmentMode?: boolean
    ): Observable<AkitCreateSessionsForExerciseResult> {
        if (!ex) return;
        return new Observable(subscriber => {
            const _exerciseResults = this.createSessions([ex], startActive, assessmentMode);
            _exerciseResults.subscribe(results => {
                if (results && results.length === 1) {
                    subscriber.next(results[0]);
                }
                subscriber.complete();
            });
        });
    }

    // create sessions for the given exercises. Each exercise is given as
    // an exercise specification or an exercise ID.
    createSessions(
        exercises: (string | AuthoredExercise)[],
        startActive?: boolean,
        assessmentMode?: boolean
    ): Observable<AkitCreateSessionsForExerciseResult[]> {
        if (!exercises || exercises.length == 0) return;

        let exList = exercises.map(_ex => {
            if (typeof _ex == "string") {
                return { exerciseId: _ex, version: "latest" };
            } else {
                return { exerciseSpec: _ex };
            }
        });

        let attributes: { [key: string]: string } = {};
        if (startActive) attributes["start-active"] = "true";

        let req = {
            exercises: exList,
            attributes: attributes,
            "api-version": 2,
        };

        if(assessmentMode) req['assessmentMode']='true';
        
        return this.http
            .post(AKIT_CREATE_SESSION_ENDPOINT, req)
            .pipe(map((resData: AkitCreateSessionResponse) => {
                return resData as AkitCreateSessionsForExerciseResult[];
            }));
    }

    getSessionScore(sessionId: string, lockSession?:boolean):Observable<ExerciseResult> {
        let req = {
            sessionId: sessionId,
            lockSession: !!lockSession
        };
        return this.http
            .post<ExerciseResult>(AKIT_SESSION_SCORE_ENDPOINT, req)
    }

    lockSession(sessionId: string): Promise<Object> {
        let req = {
            action: "LOCK",
            sessionIds: [sessionId]
        };

        return this.http
            .post(AKIT_LOCK_SESSION_ENDPOINT, req)
            .toPromise();
    }

    getSessionData(sessionId: string): Observable<any> {
        let req = {
            sessionId: sessionId
        };
        return this.http
            .post<ExerciseResult>(GET_SESSION_ENDPOINT, req)
    }

    getExerciseData(exerciseId: string): Observable<ExerciseData> {
        let req = {
            id: exerciseId
        };
        return this.http
            .post<ExerciseData>(GET_EXERCISE_DATA_ENDPOINT, req)
            .pipe(map( (obj:any) => {

                let name = '<Not set>';
                let interactions:InteractionData[] = [];
                if(obj.publishedVersions && obj.publishedVersions.length>0) {
                    let arr:any[] = obj.publishedVersions;
                    let last = arr[arr.length-1];
                    if(last.name) name = last.name;
                    if(last.interactions && last.interactions.length>0) {
                        interactions = [...last.interactions];
                    }
                }
                return {
                    name: name,
                    interactions: interactions
                }
            }));
    }

    getExerciseFromId(id: string): Promise<AuthoredExercise> {
        if(this.currentExercise && this.currentExercise.id == id) {
            return Promise.resolve(this.currentExercise);
        }

        return this.http
            .post<AuthoredExercise>(GET_EXERCISE_ENDPOINT, {
                exerciseId: id,
                version: 'latest'
            })
            .toPromise()
            .then( (spec: AuthoredExercise) => {
                this.currentExercise = spec;
                return spec;
            })
    }

    getExerciseManager(spec?: AuthoredExercise): Promise<any> {
        if(spec) {
            let exerciseManager = AlgebraKIT.getExerciseManager(spec.id);
            if(exerciseManager) {
                return Promise.resolve(exerciseManager);
            } else {
                return AlgebraKIT.setExercise(spec, defaultOptions);
            }
        } else {
            let interactionName = 'TestInteraction';
            return AlgebraKIT
                .createExercise(defaultOptions)
                .then(exerciseManager => {
                    exerciseManager.addInteraction(0, interactionName);
                    return exerciseManager;
                });
        }
        
    }

    validate(exercise: AuthoredExercise): Promise<ValidateExercisResult> {
        let req = {
            exerciseSpec: exercise,
            version: 'latest',
            generateDebugInfo: true
        };

        return this.http
            .post<ValidateExercisResult>(AKIT_VALIDATE_SESSION_ENDPOINT, req)
            .toPromise();
    }

    // cacheExercise(exercise: AuthoredExercise) {
    //     this.currentExercise = exercise;
    // }

    getExerciseFromCache() {
        return this.currentExercise;
    }

    getCurrentExerciseId() {
        if(this.currentExercise) return this.currentExercise.id;
        if(this.currentExerciseId) return this.currentExerciseId;
        return null;
    }

    setCurrentExerciseId(id: string) {
        this.currentExerciseId = id;
        if(this.currentExercise && this.currentExercise.id!=id) this.currentExercise = null;
    }

    setCurrentExercise(spec: AuthoredExercise) {
        this.currentExercise = spec;
        if(spec) this.currentExerciseId = spec.id;
    }
    
    clearCurrentExercise() {
        this.currentExercise = null;
        this.currentExerciseId = null;
    }

    createEmptyExercise():Promise<AuthoredExercise> {
        return AlgebraKIT
                .createExercise(defaultOptions)
                .then(exerciseManager => {
                    exerciseManager.addInteraction(0, 'TestInteraction');
                    let spec = exerciseManager.getExercise();
                    this.currentExercise = spec;
                    return spec;
                });
    }

    isEmptyExercise(spec: AuthoredExercise): boolean {
        if(    spec && spec.definition 
            && spec.definition.elements
                    .filter(_elm => _elm.interactions)
                    .some(_elm => 
                        _elm.interactions
                            .filter(_inter => !!_inter)
                            .some(_inter => (_inter.ans && _inter.ans.type) || _inter.interactions)
                    )
        ) {
            return false;
        }

        return true;
    }
}

